<template>
	<b-popover
		ref="popover"
		target="number"
		triggers="click blur"
		placement="right"
		@hidden="onHidden"
	>
		<template #title>
			{{ $t('Numérotation des documents') }}
		</template>

		<div v-if="lastDocument">
			<p>
				{{ $t("La numérotation doit suivre l’ordre chronologique des documents sans interruption et sans doublon. Elle n’est jamais remise à zéro, même lors d’un changement d’exercice comptable !") }}
			</p>

			<b-link
				v-if="!lastDocument || pageType === 'quotes' || is_superadmin"
				v-b-toggle.collapse-1
				variant="primary"
				:class="{ 'superadmin': is_superadmin && pageType === 'invoices' }"
			>
				{{ $t("Modifier le format") }}
			</b-link>
			<p v-else>
				{{ $t("Vous ne pouvez plus modifier le numéro car vous avez déjà confirmé et comptabilisé une facture.") }}<br>
				{{ $t("Vous pensez devoir modifier votre numéro ?") }}<br>
				{{ $t("Vous avez fait une erreur ?") }}<br>
				{{ $t("Contactez notre Service Client.") }}
			</p>
		</div>

		<b-collapse
			id="collapse-1"
			class="mt-3"
			:visible="!lastDocument"
		>
			<b-row>
				<b-col id="prefix_anchor" cols="12">
					<label>{{ $t("Préfix") }}</label>
					<b-form-input
						v-model="prefix"
						size="sm"
						class="billing__input billing__input--solid"
					/>
					<b-tooltip
						placement="top"
						target="prefix_anchor"
						container="body"
					>
						{{ $t("Le Préfix est une donnée fixe (F ou D). Il ne doit pas comporter d’année ou de mois. Pour intégrer l’année ou le mois au numéro, cocher la case souhaitée dans Format.") }}
					</b-tooltip>
				</b-col>

				<b-col
					cols="12"
					class="mt-3"
				>
					<label>{{ $t("Séparateur") }}</label>
					<b-form-select
						v-model="separator"
						size="sm"
						class="billing__input billing__input--solid"
					>
						<b-form-select-option
							v-for="(itemSeparator, index) in separators"
							:key="index"
							:value="itemSeparator"
						>
							{{ itemSeparator }}
						</b-form-select-option>
					</b-form-select>
				</b-col>

				<b-col
					v-if="!lastDocument"
					cols="12"
					class="mt-3"
				>
					<label>{{ $t("Chiffre de démarrage du compteur") }}</label>
					<b-form-input
						v-model="start"
						size="sm"
						class="billing__input billing__input--solid"
					/>
				</b-col>

				<b-col
					cols="12"
					class="mt-3"
				>
					<label>{{ $t("Longueur") }}</label>
					<b-form-input
						v-model="length"
						size="sm"
						type="number"
						class="billing__input billing__input--solid"
						required
					/>
				</b-col>

				<b-col
					cols="12"
					class="mt-3"
				>
					<label>{{ $t("Format") }}</label>
					<b-card style="background-color: transparent">
						<b-form-radio-group
							id="radio-group-1"
							v-model="format"
							:options="formatValueArray"
							:stacked="true"
							name="format-options"
							label="Format"
						/>
					</b-card>
				</b-col>

				<b-col
					cols="12"
					class="mt-3"
				>
					<s-button type="" @click="handleSubmit"> {{ $t("Enregistrer") }} </s-button>
				</b-col>
			</b-row>
		</b-collapse>
	</b-popover>
</template>

<script>
	import { query } from "sinao-corejs";
	import { mapState } from "vuex";

	export default {
		props: {
			value: {
				type: null,
				required: true
			},
			popoverShow: {
				type: Boolean,
				required: true
			},
			nextDocument: {
				type: null,
				required: true
			}
		},
		data() {
			return {
				separators: ["", "-", "/", "_", "*", "|", "#", "~"],
				formats: [
					"{prefix}{separator}{start}",
					"{prefix}{separator}{year}{separator}{start}",
					"{prefix}{separator}{year2}{separator}{start}",
					"{prefix}{separator}{year}{separator}{month}{separator}{start}",
					"{prefix}{separator}{year2}{separator}{month}{separator}{start}",
					"{prefix}{year2}{month}{separator}{start}",
					"{prefix}{separator}{month}{separator}{start}",
					"{year}{separator}{month}{separator}{start}",
					"{year2}{separator}{month}{separator}{start}",
					"{prefix}{separator}{month}{separator}{year2}{separator}{start}",
					"{prefix}{separator}{month}{separator}{year}{separator}{start}",
					"{prefix}{separator}{month}{separator}{start}",
					"{prefix}{separator}{month}{separator}{start}",
					"{year}{separator}{start}",
					"{year2}{separator}{start}",
					"{month}{separator}{start}",
					"{start}"
				],
				separator: "",
				format: "",
				start: 1,
				prefix: "",
				formatValue: "",
				length: 6
			};
		},
		computed: {
			...mapState({
				appId: state => state.app.id,
				pageType: state => state.page.type,
				lastDocument: state => state.home.lastDocument,
        		is_superadmin: state => state.home.is_superadmin,
				config: state => state.settings.config
			}),
			formatValueArray() {
				const prefix = this.prefix;
				const separator = this.separator;
				const start = this.start;
				const length = this.length;
				const newFormats = [];

				for (const format of this.formats) {
					const formatValue = this.constructBillingNumber(format, {
						prefix,
						separator,
						start,
						length
					});

					newFormats.push({ value: format, text: formatValue });
				}

				return newFormats;
			},
			computedWatchList() {
				const { prefix, separator, start, length, format } = this;

				return { prefix, separator, start, length, format };
			},
			defaultPrefix() {
				return this.config.accounting.piecenumbering[this.pageType].prefix || (this.pageType === "invoices" ? "FAC" : "DEV");
			},
            formatValuesRadios() {
                return this.formatValueArray.reduce((accumulator, currentValue) =>  {
                    accumulator.push(currentValue.value);
                    return accumulator;
                }, []);
            }
		},
		watch: {
			nextDocument: {
				handler() {
					const { format, next_number, length, prefix } = this.$props?.nextDocument ?? {};

					this.prefix = prefix ?? this.defaultPrefix;
					this.start = next_number ?? 1;
					this.length = length ?? 6;
					this.format = this.formats[0];

					if (format) {
						const _separator = format.replace(/[a-z]/gi, "")[0];

						if (_separator) {
							this.separator = _separator;
						} else {
							this.separators[0];
						}
					}

					this.formatValue = this.constructBillingNumber(this.format, {
						prefix: this.prefix,
						separator: this.separator,
						start: this.start,
						length: this.length
					});
				},
				immediate: true
			},
			computedWatchList: {
				handler() {
					this.formatValue = this.constructBillingNumber(this.format, {
						prefix: this.prefix,
						separator: this.separator,
						start: this.start,
						length: this.length
					});
				},
				immediate: false
			}
		},
		methods: {
			onHidden() {
				this.$emit("update:popoverShow", false);
				this.$refs.popover.$emit("close");
			},
			async handleSubmit() {
				if (this.length === null || this.length <= 0) {
					return this.$bvToast.toast(`La longueur doit être supérieur ou égal à 1.`, {
						title: `Erreur d'enregistrement`,
						variant: "danger",
						solid: true
					});
				}

				// Format the format
				let apiFormat = this.format.replace(/{prefix}/g, "");

				apiFormat = apiFormat.replace(/{separator}/g, this.separator);
				apiFormat = apiFormat.replace(/{year}/g, "Y");
				apiFormat = apiFormat.replace(/{year2}/g, "y");
				apiFormat = apiFormat.replace(/{month}/g, "m");
				apiFormat = apiFormat.replace(/{start}/g, "x");

				const settingKey = `accounting.piecenumbering.${this.pageType}`;
				const settings = {};

				if (!this.lastDocument) {
					settings[`${settingKey}.start`] = this.start - 1;
				}

				settings[`${settingKey}.format`] = apiFormat;
				settings[`${settingKey}.length`] = this.length;
				settings[`${settingKey}.prefix`] = this.format.includes("prefix") ? this.prefix : "";

				await query({
					operationId: "app.settings.update",
					params: {
						appId: this.appId
					},
					data: { settings }
				});

				this.$emit("input", this.formatValue);
        this.dispatchEvent({
          modalName: null,
          source: "billing",
          action: "reload_config",
          data: {  }
        });

				this.onHidden();
			}
		}
	};
</script>

<style></style>
